import {
  PointOfSalesResponse,
  type OfflinePointOfSalesResponse,
  type OnlinePointOfSalesResponse,
  getMallTitlesMap,
  PointOfSalesType,
} from '~domains/partner-application/entities/application';
import { useMalls } from '~domains/partner-application/entities/mall';
import { Card } from '~domains/partner-application/shared/ui/card';

import { Button } from '@breezeventures/ui-kit/button';
import { Typography } from '@breezeventures/ui-kit/typography';

import { AddressReviewItems } from './address-review-items';
import { DocumentReviewItem } from './document-review-item';
import { ReviewItem } from './review-item';

const storeTypeToTitle: Record<PointOfSalesType, string> = {
  ONLINE: 'Online store',
  OFFLINE: 'Physical store',
};

const StoreDetails = ({
  store,
}: {
  store: OfflinePointOfSalesResponse | OnlinePointOfSalesResponse;
}) => {
  const { data: malls = [] } = useMalls();
  const isOfflineStore = store.type === 'OFFLINE';

  const mallTitlesMap = getMallTitlesMap(malls);

  return (
    <div className="grid grid-cols-2 gap-4">
      <ReviewItem title="Type" value={storeTypeToTitle[store.type]} />
      <ReviewItem title="Store name" value={store.name} />
      <ReviewItem title="Store e-mail address" value={store.email} />
      <ReviewItem title="Store contact number" value={store.contactNumber} />

      {isOfflineStore && (
        <ReviewItem
          title="Tax Identification number (TIN)"
          value={store.tinCode}
        />
      )}

      <ReviewItem
        title="Avg sales volume per month (PHP)"
        value={store.avgMonthlySales}
      />
      <ReviewItem
        title="Website or social media page (if any)"
        value={store.websiteURL}
      />

      {isOfflineStore && (
        <>
          <ReviewItem
            title="Store size (sq.m.)"
            value={store.areaSqm}
            hideIfEmpty
          />
          <ReviewItem title="Number of floors" value={store.numberOfFloors} />
          <ReviewItem
            title="The store have CCTV cameras"
            value={store.hasCCTV ? 'Yes' : 'No'}
          />
          <ReviewItem
            title="How many days are recordings saved?"
            value={store.dayOfRecording}
            hideIfEmpty
          />

          <AddressReviewItems address={store.address} />

          <ReviewItem
            title="What mall is your store located in?"
            value={mallTitlesMap[store.mallId ?? '']}
          />

          <ReviewItem
            title="Mall name (Helps customers find you)"
            value={store.mallNameOther}
            hideIfEmpty
          />

          <div className="col-span-full flex flex-col gap-3">
            {store.photos.map((photo) => (
              <DocumentReviewItem
                key={photo.documentId}
                title="Store photo"
                document={photo}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const StoresReview = ({
  pointsOfSales,
  onEdit,
}: {
  pointsOfSales: PointOfSalesResponse[] | undefined;
  onEdit?: () => void;
}) => {
  if (!pointsOfSales?.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 pt-3">
      <div className="flex items-center justify-between gap-2">
        <Typography.Title size="2xs" tag="div">
          Stores
        </Typography.Title>
        {onEdit && (
          <Button
            onClick={onEdit}
            type="button"
            size="s"
            variant="secondary"
            aria-label="Edit Stores"
          >
            Edit
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-3">
        {pointsOfSales.map((store) => (
          <Card
            key={store.uiId}
            header={store.name}
            subheader={
              store.type === 'OFFLINE'
                ? store.address.streetAndHouse
                : store.websiteURL
            }
            expandable
          >
            <StoreDetails store={store} />
          </Card>
        ))}
      </div>
    </div>
  );
};
